import type { CompanyType, Permission, RoleId } from 'api/types';
import { UseCase } from 'api/types';

export const KNOCK_FEED_CHANNEL_ID = import.meta.env.VITE_APP_KNOCK_FEED_CHANNEL_ID as string;

export const DASHBOARD_POPUPS_PREFERENCE_KEY = 'dashboard_popups';

export enum NotificationEvent {
  INTERACTION_CLICK = 'dashboard_notifications_interaction_click',
  TAB_CLICK = 'dashboard_notifications_tab_click',
  SETTINGS_CHECKBOX_CLICK = 'dashboard_settings_notifications_checkbox_click',
  SETTINGS_SAVE_CLICK = 'dashboard_settings_notifications_save_click',
  MARK_ALL_READ_CLICK = 'dashboard_notifications_mark_all_read_click',
  BELL_CLICK = 'dashboard_notifications_click',
  SETTINGS_CLICK = 'dashboard_settings_notifications_click',
  POPUP_SHOWN = 'dashboard_notifications_popup_shown',
}

export type NotificationCategory = 'all' | 'orders' | 'team' | 'api';

export type NotificationChannel = 'email' | 'in_app_feed';

export type KnockChannelType = 'email' | 'in_app_feed' | 'http' | 'sms' | 'push' | 'chat';

export type NotificationConfigExtended = {
  knockId: string;
  title: string;
  description: string;
  tooltip?: string;
  channels: NotificationChannel[];
  type: 'category' | 'workflow';
  // Maps a UI toggle to a different Knock channel_type. Defaults to identity (email -> email).
  // The changelog digest is delivered on the http/webhook channel, so its email toggle maps there.
  channelTypeMap?: Partial<Record<NotificationChannel, KnockChannelType>>;
  visibleToPermission?: Permission;
  visibleToUseCases?: UseCase[];
  visibleToRoles?: RoleId[];
  visibleToCompanyTypes?: CompanyType[];
};

export type NotificationCategoryExtended = {
  id: string;
  title: string;
  notifications: NotificationConfigExtended[];
};

export type ChannelPreferences = Record<NotificationChannel, boolean>;
export type LocalPreferences = Record<string, ChannelPreferences>;

export type PreferencesFormData = {
  notifications: LocalPreferences;
  showNotificationPopups: boolean;
};

export const NOTIFICATION_CATEGORIES: NotificationCategoryExtended[] = [
  {
    id: 'orders',
    title: 'Orders',
    notifications: [
      {
        knockId: 'order_updates',
        title: 'Order updates',
        description:
          'Get notified when an order you created or were added to completes, expires, or gets updated in any other way.',
        channels: ['email', 'in_app_feed'],
        type: 'category',
        visibleToPermission: 'can_view_orders',
      },
      {
        knockId: 'aso_orders',
        title: 'Automatically created orders',
        description: 'Get notified when an order is created in Encompass via ASO and your role is a recipient.',
        channels: ['email', 'in_app_feed'],
        type: 'category',
        visibleToPermission: 'can_view_orders',
        visibleToUseCases: [UseCase.MORTGAGE_LENDING],
      },
      {
        knockId: 'refresh_reconnection',
        title: 'Refreshes requiring reconnection',
        description: 'Get notified when a refreshed order requires the end-user to reconnect their accounts.',
        channels: ['email', 'in_app_feed'],
        type: 'category',
        visibleToPermission: 'can_view_orders',
      },
    ],
  },
  {
    id: 'team',
    title: 'Team',
    notifications: [
      {
        knockId: 'members_pending_approval',
        title: 'Members pending approval',
        description: 'Get notified when a team member joins the Truv Dashboard and requires approval.',
        channels: ['email', 'in_app_feed'],
        type: 'category',
        visibleToPermission: 'can_invite_users',
      },
    ],
  },
  {
    id: 'api',
    title: 'API',
    notifications: [
      {
        knockId: 'api_prod_keys',
        title: 'Production keys',
        description: 'Get notified when a production API key is issued or rotated.',
        channels: ['email', 'in_app_feed'],
        type: 'category',
        visibleToPermission: 'can_view_keys',
      },
    ],
  },
  {
    id: 'truv_updates',
    title: 'Truv updates',
    notifications: [
      {
        knockId: 'changelog',
        title: 'Changelog',
        description: 'Get notified about Truv API changes and feature enhancements.',
        channels: ['email'],
        type: 'category',
        channelTypeMap: { email: 'http' },
        visibleToRoles: ['owner', 'administrator', 'developer'],
        visibleToCompanyTypes: ['client'],
      },
    ],
  },
];
